/**
 * ER7 line endings: the boundary between what a textarea can hold and what HL7
 * requires on the wire.
 *
 * HL7 v2 separates segments with CR (0x0D). A browser `<textarea>` cannot
 * produce one: per the HTML spec the `value` getter normalizes every newline to
 * LF, so anything a user pastes or types arrives here LF-separated. Sending that
 * verbatim puts a malformed message on a partner socket.
 *
 * That bug is invisible without this module, which is why it is worth its own
 * file. The harness tolerates LF everywhere it splits (`mllp/msh.ts`,
 * `mllp/ack.ts`, `store/messages.ts`), so on the dev loopback a corrupt message
 * is acknowledged AA with the control id echoed; `bytes` is unchanged because CR
 * and LF are both one byte; and the detail dialog renders the harness's
 * pre-split `segments`, which look clean either way. Nothing on screen can tell
 * a correct message from a broken one. Only the bytes can.
 *
 * The convention this establishes: **LF in the editor, CR at the wire**.
 * `toEditorText` on the way in, `toWireEr7` on the way out, and the `Er7Wire`
 * brand on the request types so a panel cannot skip the conversion without
 * failing `pnpm lint`.
 */
import type { Er7Wire } from './types'

/** HL7 v2 segment terminator. Not configurable: the standard fixes it at CR. */
export const SEGMENT_SEPARATOR = '\r'

/**
 * Every newline form, consumed as ONE separator each.
 *
 * The same expression the harness's `splitSegments` uses. Order matters: CRLF
 * has to match before the bare alternatives, or a CRLF paste yields an empty
 * segment between every real one.
 */
const NEWLINE = /\r\n|\r|\n/

/**
 * A BOM survives a copy out of Word or a Windows editor and is invisible on
 * screen, but it makes `segment.startsWith('MSH')` false and the harness answers
 * "message does not begin with an MSH segment" for something that looks perfect.
 *
 * Written as the escape, not the character: a literal U+FEFF in source is
 * invisible to the next reader and to a diff.
 */
const LEADING_JUNK = /^[\uFEFF\s]+/

/**
 * The segments that will go on the wire, in order.
 *
 * Exported separately from `toWireEr7` because the UI shows the segment count,
 * and a count derived from anything but the function that builds the payload can
 * drift away from what is actually sent.
 *
 * Empty and whitespace-only segments are dropped. That covers leading blank
 * lines, interior blank lines from a PDF or rendered-markdown paste, and a
 * trailing newline, in one rule. HL7 permits no empty segment, so none can be
 * intentional.
 *
 * Whitespace INSIDE a segment is left alone. Trailing spaces in a field are data
 * and we cannot know they are not.
 */
export function wireSegments(text: string): string[] {
  return text
    .replace(LEADING_JUNK, '')
    .split(NEWLINE)
    .filter((segment) => segment.trim() !== '')
}

/**
 * Editor text to the exact bytes for `message` on `/send` or `/mimic/inbound`.
 *
 * Every segment ends with CR, the last one included: HL7 v2 makes CR a segment
 * terminator, not only a separator. The `<CR>` in the MLLP trailer `<FS><CR>`
 * that `encodeFrame` appends ends the frame, not the last segment, so a correct
 * frame ends `…<CR><FS><CR>`. MedStar's Epic refused messages from this screen
 * for the missing final CR ("segment terminator not received", 2026-10-16), and
 * a textarea cannot hold a CR, so the user had no way to add it by hand.
 *
 * A payload already terminated with CR passes through byte-identical. A fixture
 * as `GET /fixtures/{type}` returns it lacks the final CR and gains one.
 * Blank input still returns '' (no segments, so nothing to terminate).
 *
 * This is the one place `Er7Wire` is minted, and the cast is the whole point:
 * it is an assertion that the terminators are now correct.
 */
export function toWireEr7(text: string): Er7Wire {
  return wireSegments(text)
    .map((segment) => `${segment}${SEGMENT_SEPARATOR}`)
    .join('') as Er7Wire
}

/**
 * Wire ER7 to something a `<textarea>` can hold.
 *
 * Needed for a subtler case than display. `getFixture` returns CR-separated
 * text; put that straight into React state and the DOM node reports LF back from
 * the first render, so the first keystroke reconciles state down to LF and
 * silently corrupts a message that was correct. Normalizing on the way in means
 * the editor value never changes representation under the user.
 */
export function toEditorText(er7: string): string {
  return er7.split(NEWLINE).join('\n')
}

/**
 * MSH-9.1, the message code (`ORU`, `ORM`, `ADT`). Null when unreadable.
 *
 * Reads the field separator from MSH-1 and the component separator from MSH-2
 * rather than assuming `|` and `^`, mirroring the harness's `parseMsh`: a sender
 * is entitled to declare its own, and assuming them mis-parses the message
 * silently.
 *
 * Only used for a non-blocking hint. Real validation stays server-side, where
 * the harness already answers 400 with a specific reason.
 */
export function messageTypeCode(text: string): string | null {
  const [segment = ''] = wireSegments(text)
  if (!segment.startsWith('MSH')) return null

  const fieldSeparator = segment.charAt(3)
  if (fieldSeparator === '') return null

  // MSH-1 is the separator character itself, so MSH-n lands at index n-1.
  const fields = segment.split(fieldSeparator)
  const componentSeparator = (fields[1] ?? '').charAt(0) || '^'
  const code = (fields[8] ?? '').split(componentSeparator)[0] ?? ''

  return code === '' ? null : code
}
